import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';

export interface SharedFileInfo {
  path: string;
  name: string;
  mime: string;
}

interface NativeSharePlugin {
  getPending(): Promise<{ files: SharedFileInfo[] }>;
  clearPending(): Promise<void>;
  addListener(
    eventName: 'sharedFiles',
    listener: (data: { files: SharedFileInfo[] }) => void,
  ): Promise<PluginListenerHandle>;
}

export const nativeShare = registerPlugin<NativeSharePlugin>('NativeShare');

export async function readSharedFile(info: SharedFileInfo): Promise<File> {
  const result = await Filesystem.readFile({
    path: info.path,
    directory: Directory.Cache,
  });
  const base64 =
    typeof result.data === 'string'
      ? result.data
      : await result.data.text();
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const file = new File([bytes], info.name, {
    type: info.mime || 'application/octet-stream',
  });
  await Filesystem.deleteFile({
    path: info.path,
    directory: Directory.Cache,
  }).catch(() => undefined);
  return file;
}

export async function getPendingSharedFiles(): Promise<SharedFileInfo[]> {
  try {
    const result = await nativeShare.getPending();
    return result.files ?? [];
  } catch {
    return [];
  }
}

export async function clearPendingSharedFiles(): Promise<void> {
  await nativeShare.clearPending().catch(() => undefined);
}
